import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Edit2,
  Lock,
  Eye,
  EyeOff,
  Check,
  X,
  UserCircle,
  CreditCard,
  Shield,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import { toast } from "react-toastify";
import { getSubscriptionHistory, getUserCredits } from '../apis/subscription';
import SubscriptionCard from "../components/SubscriptionCard";
import SubscriptionModal from "../components/SubscriptionModal";
import CreditPurchaseModal from "../components/CreditPurchaseModal";
import { useNavigate } from "react-router-dom";
import 'jspdf-autotable';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const navigate = useNavigate();

  const loadSubscriptionHistory = async () => {
    try {
      setSubscriptionsLoading(true);
      const response = await getSubscriptionHistory();
      if (response && response.data && response.data.success) {
        setCurrentSubscription(response.data.data.current_subscription);
        const sortedSubscriptions = response.data.data.history.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setSubscriptions(sortedSubscriptions);
      }
    } catch (error) {
      console.error("Error fetching subscription history:", error);
      toast.error("Failed to load subscription history");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const loadUserCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await getUserCredits();
      if (response && response.data && response.data.success) {
        setUserCredits(response.data.data);
      }
    } catch (error) {
      console.error("Error loading user credits:", error);
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    const user = localStorage.getItem("user");
    loadSubscriptionHistory();
    loadUserCredits();
    if (user) {
      const parsed = JSON.parse(user);
      setUserData(parsed);
      setForm({
        name: parsed.name || "",
        email: parsed.email || "",
        phone: parsed.phone || "",
      });
    }
  }, []);

  // Check if user has password
  const hasPassword = userData?.hasPassword ?? true; // Default to true for backward compatibility

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    if (passwordError) setPasswordError("");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const updateUserDetails = async () => {
    setStatusMessage("");
    setIsLoading(true);
    try {
      const token = await localStorage.getItem("userAccessToken");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/updateUser`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        }
      );

      const data = await res.json();

      if (!data.success) throw new Error(data.error || "Failed to update");

      setUserData(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      setIsEditing(false);
      setStatusMessage("Profile updated successfully.");
      toast.success("Profile updated successfully!");

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setStatusMessage(err.message);
      toast.error(err.message || "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async () => {
    setPasswordError("");
    
    // Validate new password
    if (!passwordForm.newPassword) {
      setPasswordError("New password is required");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long");
      return;
    }

    // If user has password, old password is required
    if (hasPassword && !passwordForm.oldPassword) {
      setPasswordError("Current password is required");
      return;
    }

    setIsPasswordLoading(true);
    try {
      const token = localStorage.getItem("userAccessToken");

      // Prepare request body - only include oldPassword if user has password
      const requestBody = hasPassword 
        ? { oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword }
        : { newPassword: passwordForm.newPassword };

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/updatePassword`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await res.json();

      if (!data.success)
        throw new Error(data.error || "Failed to update password");

      setShowPasswordModal(false);
      setPasswordForm({ oldPassword: "", newPassword: "" });

      const successMessage = hasPassword 
        ? "Password changed successfully! You will be logged out for security."
        : "Password added successfully! You will be logged out for security.";

      toast.success(successMessage);
      
      // Update userData to reflect password was added
      if (!hasPassword && userData) {
        const updatedUser = { ...userData, hasPassword: true };
        setUserData(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      setTimeout(() => {
        handleLogout();
      }, 2000);
    } catch (err) {
      setPasswordError(err.message);
      toast.error(err.message || "Failed to update password");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  if (!userData) {
    return (
      <div className="min-h-screen bg-[#f3f3f3] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-gray-300 border-t-gray-900 rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fff] p-4 mt-5">
      {/* Desktop Layout */}
      <div className="hidden lg:block max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-sm p-4 mb-4 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm border border-gray-200 bg-blue-50"
                >
                  <UserCircle className="w-8 h-8 text-blue-600" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-bold mb-0.5 text-gray-900">{userData.name}</h1>
                  <p className="text-sm text-gray-600 font-medium">
                    Manage your account settings
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-4 py-2 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-600" />
                    <span className="text-sm font-bold text-emerald-700">Active Account</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Status Message */}
          <AnimatePresence>
            {statusMessage && (
              <motion.div
                initial={{ opacity: 0, y: -16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.95 }}
                className={`mb-4 p-4 rounded-2xl text-sm border ${
                  statusMessage.includes("successfully")
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  {statusMessage.includes("successfully") ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <X className="w-5 h-5" />
                  )}
                  <span className="font-semibold">{statusMessage}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* AI Credits Section - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#048AAD]/10 to-[#01CC54]/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-gradient-to-r from-[#048AAD] to-[#01CC54] rounded-xl">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">
                      AI Credits
                    </h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowCreditModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-[#048AAD] to-[#01CC54] text-white rounded-xl text-sm font-bold hover:opacity-90 transition shadow-sm flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Buy Credits
                  </motion.button>
                </div>
              </div>

              <div className="p-4">
                {creditsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-10 h-10 border-4 border-gray-300 border-t-gray-900 rounded-full"
                    />
                  </div>
                ) : userCredits && userCredits.totalCredits > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Total Summary */}
                    <div className="p-4 bg-gradient-to-r from-[#048AAD]/10 to-[#01CC54]/10 rounded-xl border border-[#048AAD]/20">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Total Available</div>
                      <div className="text-3xl font-bold text-[#048AAD] mb-1">
                        {userCredits.totalAvailableReplies}
                      </div>
                      <div className="text-xs text-gray-600">AI Replies</div>
                      {userCredits.batches?.[0]?.creditPlan && (
                        <div className="mt-2 pt-2 border-t border-[#048AAD]/20 text-xs text-gray-600">
                          {userCredits.totalCredits} credits (1 credit = {userCredits.batches[0].creditPlan.per_credit_ai_replies} replies)
                        </div>
                      )}
                    </div>

                    {/* Credit Batches List */}
                    <div className="md:col-span-2">
                      <h3 className="text-sm font-bold text-gray-700 mb-3">Credit Batches</h3>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                        {userCredits.batches?.map((batch, index) => (
                          <motion.div
                            key={batch.id || index}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-gray-900">
                                {batch.creditPlan?.plan_name || 'Credit Plan'}
                              </span>
                              <span className="text-sm font-bold text-[#048AAD]">
                                {batch.available_credits} credits
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>
                                {batch.available_credits * (batch.creditPlan?.per_credit_ai_replies || 1)} AI replies
                              </span>
                              {batch.expires_at ? (
                                <span>
                                  Expires: {new Date(batch.expires_at).toLocaleDateString('en-IN', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </span>
                              ) : (
                                <span className="text-emerald-600 font-semibold">Lifetime</span>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-gray-200">
                      <Sparkles className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-base font-bold mb-1 text-gray-900">No Credits Available</h3>
                    <p className="text-sm text-gray-600 font-medium mb-4">
                      Purchase credits to use AI chatbot features
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowCreditModal(true)}
                      className="px-6 py-2.5 bg-gradient-to-r from-[#048AAD] to-[#01CC54] text-white rounded-xl font-bold hover:opacity-90 transition shadow-sm flex items-center gap-2 mx-auto"
                    >
                      <Sparkles className="w-4 h-4" />
                      Buy Credits
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-12 gap-4">
            {/* Profile Information - Left Side */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="col-span-5"
            >
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden h-fit">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-600 rounded-xl">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-base font-bold text-gray-900">Personal Information</h2>
                  </div>
                </div>
                <div className="p-4">
                  <AnimatePresence mode="wait">
                    {isEditing ? (
                      <motion.div
                        key="editing"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                      >
                        {/* Name */}
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                            <User className="w-4 h-4 text-blue-600" />
                            Full Name
                          </label>
                          <input
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            type="text"
                            className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            placeholder="Enter your full name"
                          />
                        </div>
                        {/* Email */}
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                            <Mail className="w-4 h-4 text-blue-600" />
                            Email
                          </label>
                          <input
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            type="email"
                            className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            placeholder="Enter your email"
                          />
                        </div>
                        {/* Phone */}
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                            <Phone className="w-4 h-4 text-blue-600" />
                            Phone
                          </label>
                          <input
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            type="text"
                            className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                            placeholder="Enter your phone number"
                          />
                        </div>
                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={updateUserDetails}
                            disabled={isLoading}
                            className="flex-1 text-gray-900 py-3 text-sm rounded-xl font-bold shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-gray-300"
                            style={{ backgroundColor: "#eefa94" }}
                          >
                            {isLoading ? (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 1,
                                  repeat: Infinity,
                                  ease: "linear",
                                }}
                                className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full"
                              />
                            ) : (
                              <>
                                <Check className="w-5 h-5" />
                                <span>Save Changes</span>
                              </>
                            )}
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              setIsEditing(false);
                              setForm({
                                name: userData.name,
                                email: userData.email,
                                phone: userData.phone,
                              });
                              setStatusMessage("");
                            }}
                            className="flex-1 bg-white text-gray-700 py-3 text-sm rounded-xl font-bold border border-gray-300 hover:bg-gray-50 transition flex items-center justify-center gap-2"
                          >
                            <X className="w-5 h-5" />
                            Cancel
                          </motion.button>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="viewing"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-3"
                      >
                        {/* Info Items */}
                        {[
                          { Icon: User, label: "Full Name", value: userData.name },
                          { Icon: Mail, label: "Email", value: userData.email },
                          { Icon: Phone, label: "Phone", value: userData.phone },
                          {
                            Icon: Calendar,
                            label: "Member Since",
                            value: new Date(userData.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }),
                          },
                        ].map(({ Icon, label, value }) => (
                          <motion.div
                            key={label}
                            whileHover={{ scale: 1.01 }}
                            className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200"
                          >
                            <div className="p-2 bg-white rounded-lg border border-gray-200">
                              <Icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-bold text-gray-600 mb-0.5">{label}</p>
                              <p className="text-sm font-bold text-gray-900 truncate">{value}</p>
                            </div>
                          </motion.div>
                        ))}
                        {/* Actions */}
                        <div className="flex gap-3 pt-3">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setIsEditing(true)}
                            className="flex-1 text-gray-900 py-3 text-sm rounded-xl font-bold shadow-sm hover:shadow-md transition flex items-center justify-center gap-2 border border-gray-300"
                            style={{ backgroundColor: "#eefa94" }}
                          >
                            <Edit2 className="w-5 h-5" />
                            Edit Profile
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setShowPasswordModal(true)}
                            className="flex-1 bg-white text-gray-700 py-3 text-sm rounded-xl font-bold border border-gray-300 hover:bg-gray-50 transition flex items-center justify-center gap-2"
                          >
                            <Lock className="w-5 h-5" />
                            {hasPassword ? "Change Password" : "Add Password"}
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>

            {/* Subscription History - Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="col-span-7"
            >
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-600 rounded-xl">
                          <CreditCard className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-base font-bold text-gray-900">
                          Subscription History
                        </h2>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowPlansModal(true)}
                        className="px-4 py-2 bg-[#048AAD] text-white rounded-xl text-sm font-bold hover:bg-[#048AAD]/90 transition shadow-sm flex items-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        {currentSubscription ? 'Upgrade / Renew' : 'Subscribe Now'}
                      </motion.button>
                    </div>
                  </div>

                <div className="p-4">
                  {subscriptionsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-10 h-10 border-4 border-gray-300 border-t-gray-900 rounded-full"
                      />
                    </div>
                  ) : subscriptions.length > 0 ? (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                      {subscriptions.map((subscription, index) => (
                        <SubscriptionCard
                          key={subscription.id}
                          subscription={subscription}
                          index={index}
                        />
                      ))}
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-16"
                    >
                      <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-200">
                        <CreditCard className="w-10 h-10 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-bold mb-2 text-gray-900">No Subscriptions Found</h3>
                      <p className="text-sm text-gray-600 font-medium">
                        You haven't purchased any subscription plans yet.
                      </p>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-sm p-4 mb-4 border border-gray-200"
          >
            <div className="flex items-center gap-3 mb-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm border border-gray-200 bg-blue-50"
              >
                <UserCircle className="w-7 h-7 text-blue-600" />
              </motion.div>
              <div className="truncate flex-1">
                <h1 className="text-lg font-bold mb-0.5 truncate text-gray-900">
                  {userData.name}
                </h1>
                <p className="text-xs text-gray-600 font-medium">
                  Account Settings
                </p>
              </div>
            </div>
            <div className="px-3 py-2 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700">Active Account</span>
              </div>
            </div>
          </motion.div>

          {/* Status Message */}
          <AnimatePresence>
            {statusMessage && (
              <motion.div
                initial={{ opacity: 0, y: -16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -16, scale: 0.95 }}
                className={`mb-3 p-3 rounded-2xl text-xs border ${
                  statusMessage.includes("successfully")
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  {statusMessage.includes("successfully") ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  <span className="font-semibold">{statusMessage}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-4"
          >
            <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-600 rounded-lg">
                  <User className="w-3.5 h-3.5 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Personal Information</h2>
              </div>
            </div>
            <div className="p-4">
              <AnimatePresence mode="wait">
                {isEditing ? (
                  <motion.div
                    key="editing"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-3"
                  >
                    {/* Name */}
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                        <User className="w-3.5 h-3.5 text-blue-600" />
                        Full Name
                      </label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        type="text"
                        className="w-full px-3 py-2.5 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter your full name"
                      />
                    </div>
                    {/* Email */}
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                        <Mail className="w-3.5 h-3.5 text-blue-600" />
                        Email
                      </label>
                      <input
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        type="email"
                        className="w-full px-3 py-2.5 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter your email"
                      />
                    </div>
                    {/* Phone */}
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                        <Phone className="w-3.5 h-3.5 text-blue-600" />
                        Phone
                      </label>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        type="text"
                        className="w-full px-3 py-2.5 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={updateUserDetails}
                        disabled={isLoading}
                        className="w-full text-gray-900 py-2.5 text-sm rounded-xl font-bold shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-gray-300"
                        style={{ backgroundColor: "#eefa94" }}
                      >
                        {isLoading ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                            className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full"
                          />
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            <span>Save Changes</span>
                          </>
                        )}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setIsEditing(false);
                          setForm({
                            name: userData.name,
                            email: userData.email,
                            phone: userData.phone,
                          });
                          setStatusMessage("");
                        }}
                        className="w-full bg-white text-gray-700 py-2.5 text-sm rounded-xl font-bold border border-gray-300 hover:bg-gray-50 transition flex items-center justify-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="viewing"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-3"
                  >
                    {/* Info Items */}
                    {[
                      { Icon: User, label: "Full Name", value: userData.name },
                      { Icon: Mail, label: "Email", value: userData.email },
                      { Icon: Phone, label: "Phone", value: userData.phone },
                      {
                        Icon: Calendar,
                        label: "Member Since",
                        value: new Date(userData.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }),
                      },
                    ].map(({ Icon, label, value }) => (
                      <motion.div
                        key={label}
                        whileHover={{ scale: 1.01 }}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200"
                      >
                        <div className="p-2 bg-white rounded-lg border border-gray-200">
                          <Icon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-600">{label}</p>
                          <p className="text-sm font-bold text-gray-900 truncate">{value}</p>
                        </div>
                      </motion.div>
                    ))}
                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsEditing(true)}
                        className="w-full text-gray-900 py-2.5 text-sm rounded-xl font-bold shadow-sm hover:shadow-md transition flex items-center justify-center gap-2 border border-gray-300"
                        style={{ backgroundColor: "#eefa94" }}
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Profile
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowPasswordModal(true)}
                        className="w-full bg-white text-gray-700 py-2.5 text-sm rounded-xl font-bold border border-gray-300 hover:bg-gray-50 transition flex items-center justify-center gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        {hasPassword ? "Change Password" : "Add Password"}
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Subscription History Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-600 rounded-lg">
                    <CreditCard className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">
                    Subscription History
                  </h2>
                </div>
              </div>

              <div className="p-4">
                {subscriptionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full"
                    />
                  </div>
                ) : subscriptions.length > 0 ? (
                  <div className="space-y-3">
                    {subscriptions.map((subscription, index) => (
                      <SubscriptionCard
                        key={subscription.id}
                        subscription={subscription}
                        index={index}
                      />
                    ))}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-gray-200">
                      <CreditCard className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-base font-bold mb-2 text-gray-900">No Subscriptions Found</h3>
                    <p className="text-sm text-gray-600 font-medium">
                      You haven't purchased any subscription plans yet.
                    </p>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200"
            >
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-600 rounded-xl">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">
                    {hasPassword ? "Change Password" : "Add Password"}
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-4 bg-white">
                {/* Old Password - Only show if user has password */}
                {hasPassword && (
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700">Current Password</label>
                    <div className="relative">
                      <input
                        type={showOldPassword ? "text" : "password"}
                        name="oldPassword"
                        value={passwordForm.oldPassword}
                        onChange={handlePasswordChange}
                        className="w-full px-4 py-3 pr-10 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowOldPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-blue-600"
                      >
                        {showOldPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {/* New Password */}
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">
                    {hasPassword ? "New Password" : "Password"}
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-3 pr-10 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-blue-600"
                    >
                      {showNewPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
                {/* Error Message */}
                <AnimatePresence>
                  {passwordError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl flex items-center gap-2 text-sm font-semibold"
                    >
                      <X className="w-4 h-4" />
                      <span>{passwordError}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={changePassword}
                    disabled={isPasswordLoading}
                    className="flex-1 text-gray-900 py-3 text-sm rounded-xl font-bold shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-gray-300"
                    style={{ backgroundColor: "#eefa94" }}
                  >
                    {isPasswordLoading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full"
                      />
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        {hasPassword ? "Update Password" : "Add Password"}
                      </>
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordForm({ oldPassword: "", newPassword: "" });
                      setPasswordError("");
                    }}
                    className="flex-1 bg-white text-gray-700 py-3 text-sm rounded-xl font-bold hover:bg-gray-50 border border-gray-300 transition flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        onSuccess={() => {
          setShowPlansModal(false);
          loadSubscriptionHistory();
          toast.success("Subscription updated successfully!");
        }}
      />
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={() => {
          loadUserCredits();
          loadSubscriptionHistory();
        }}
      />
    </div>
  );
};

export default Profile;
